/**
 * The tier step (M4 spec §10.2, plan I4): promotes contacts near the player to real ECS ships flown by the
 * traffic pilot, and demotes them back to kinematic contacts.
 *
 * - **Promote** a visible contact (not `isDockedLike`: docked, or a hidden stand-by, BL-1a) within `promoteM` of the player when its step wants a real ship: mining, holding,
 *   or a leg within `cruiseClearM` of its start or `approachDropM` of its target (a cruising contact stays a
 *   contact). Closest first, while the real ships' weight stays within `maxRealShips` (M10 P1e3: each ship counts its
 *   band's `bands.json localWeight`, an L 2, cached per def index in `TierState.defWeight`; the first candidate that
 *   does not fit waits, and so does every one behind it). **The player corporation's own ships come first**
 *   (M6 plan P22: `store.own`): every own candidate sorts ahead of every other, and at the cap an own ship still
 *   waiting takes the place of the farthest calm non-own real ship (one swap per tick) when that frees room for its
 *   weight (an own L does not swap in for one weight-1 stranger). The ship spawns with `spawnShip(..., { team,
 *   faOn: true })` at the contact's position (an S/M still inside the station it leaves: at the bay mouth, nose out,
 *   SC-5d1; a moor-band hull whose leg starts at a station: at its mooring-ring slot, `ai/traffic.ts moorSpawnPoint`;
 *   while that station's bay isn't ready it waits as a contact, `MOOR_WAIT`,
 *   M10 P3 W4b), `Hull = hp × def.hull`, and gets `Agent` + `TrafficPilot`. A kitted agent (`env.kitOf`, M10 P4b4b)
 *   flies its kit def slot (`ships/kitSlots.ts`, made at boot) and gets its kit name. An own ship
 *   flies on its slot's team (the player's, team 0), so player and corporation fire never hurt each other; the
 *   env's `onPromoted` hook then dresses it (M6 presence: the corporation livery after P23).
 * - **Sync** each tick: the store's position and hp follow the real ship (`Pos64`, `Hull / def.hull`).
 * - **Demote** beyond `demoteM` after `demoteCalmS` out of combat, or when the pilot hands the ship back to cruise.
 *   A demoted leg continues from where the ship is (from = its position, t0 = now), so the contact does not jump.
 * - **Done** (the pilot docked or finished): the ship despawns and the step is marked arrived (reported by
 *   `steps.ts`).
 *
 * `stepTiers` allocates nothing on a quiet tick (scratch typed arrays, an insertion sort); a promotion or demotion
 * is a rare event (Rapier's binding allocates in `spawnShip`/`despawnShip`).
 */
import { addComponent, commitRemovals, entityExists } from 'bitecs'
import { beginTrafficPilot, MOOR_WAIT, moorSpawnPoint, moorsAt, type TrafficBay, type TrafficPilotEnv } from '../ai/traffic'
import type { Quat, Vec3 } from '../core/math'
import { logger } from '../core/logger'
import { localWeightOf } from '../data/bands'
import { quatFromMatrix } from '../docking/bay'
import { kitShipName } from '../ships/kitOverlay'
import { kitDefIndex, setKitName } from '../ships/kitSlots'
import type { Kit } from '../ships/kits'
import { despawnShip, spawnShip, type Sim } from '../sim'
import type { LocalConfig } from '../strategic/schemas'
import { Hull, Life, MAX_ENTITIES, Pos64, Ship } from '../world/components'
import { Agent, TrafficPilot, TRAFFIC_PHASE } from './components'
import { isDockedLike, STEP_KIND, type ContactStore } from './contacts'
import { markArrived } from './steps'

export type TierConfig = Pick<LocalConfig, 'maxRealShips' | 'promoteM' | 'demoteM' | 'demoteCalmS' | 'cruiseClearM' | 'approachDropM'>

export interface TierEnv {
  readonly store: ContactStore
  readonly cfg: TierConfig
  readonly pilot: TrafficPilotEnv
  /** Ship def index by id (`sim.defs`), −1 when unknown. */
  defIndexOf(shipDef: string): number
  /** M10 P4b4b: contact `slot`'s operator kit (`subsystem.ts kitOfSlot`); unbound or null → the base def. Rare. */
  kitOf?: (slot: number) => Kit | null
  /** Called after a ship is promoted (rare; M6 presence dresses own ships, `fleet-link/presence.ts`). */
  onPromoted?: (eid: number, slot: number) => void
}

export interface TierState {
  /** The real ships' weight against `maxRealShips` (each ship its band's `localWeight`; M10 P1e3). */
  real: number
  /** `localWeight` per def index, 0 until first read (`shipWeight`). */
  readonly defWeight: Uint8Array
  /** The weight each real ship counted at promotion, by entity id (taken off again on demote or kill). */
  readonly eidWeight: Uint8Array
  /** Scratch: candidate slots and their squared distances (store capacity). */
  readonly cand: Int32Array
  readonly candD: Float64Array
  readonly warned: Set<string>
}

/** Def indices with a cached weight; a def past this (none today) reads `bands.json` each time. */
const DEF_WEIGHT_SLOTS = 256

export function createTierState(capacity: number): TierState {
  return {
    real: 0,
    defWeight: new Uint8Array(DEF_WEIGHT_SLOTS),
    eidWeight: new Uint8Array(MAX_ENTITIES),
    cand: new Int32Array(capacity),
    candD: new Float64Array(capacity),
    warned: new Set(),
  }
}

/** Ship def `def`'s weight against `maxRealShips` (its band's `localWeight`); an unknown def (−1) weighs 1. Allocation-free. */
export function shipWeight(sim: Sim, st: TierState, def: number): number {
  if (def < 0) return 1
  if (def >= st.defWeight.length) return localWeightOf(sim.defs[def].sizeBand)
  let w = st.defWeight[def]
  if (w === 0) {
    w = localWeightOf(sim.defs[def].sizeBand)
    st.defWeight[def] = w
  }
  return w
}

/** Scratch: a moor-band ship's ring spawn point, or an S/M ship's bay mouth (SC-5d1). */
const spawnAt: Vec3 = { x: 0, y: 0, z: 0 }
/** Scratch (SC-5d1): the bay of the station a leg leaves, and the nose-out spawn rotation. */
const mouthBay: TrafficBay = { frame: null, pos: { x: 0, y: 0, z: 0 }, radius: 0 }
const spawnRot: Quat = { x: 0, y: 0, z: 0, w: 1 }

/**
 * SC-5d1: true when contact `slot` is on a leg leaving a station (`bays.bayAt` on the leg's start: its bay mouth,
 * or the centre when the leg ends are unbound) and is still inside that station's bounding sphere. It then spawns at
 * the bay mouth (`spawnAt`), nose out along the bay axis and up along the bay's up (`spawnRot`), never in the hull.
 * For S/M hulls (moor-band hulls use their ring slot). Rare (promotion).
 */
function mouthSpawn(env: TierEnv, slot: number): boolean {
  const s = env.store
  const bays = env.pilot.bays
  if (s.kind[slot] !== STEP_KIND.leg || bays === null || bays.bayAt === undefined) return false
  if (!bays.bayAt(s.fx[slot], s.fy[slot], s.fz[slot], mouthBay) || mouthBay.frame === null) return false
  const dx = s.x[slot] - mouthBay.pos.x
  const dy = s.y[slot] - mouthBay.pos.y
  const dz = s.z[slot] - mouthBay.pos.z
  if (dx * dx + dy * dy + dz * dz > mouthBay.radius * mouthBay.radius) return false
  const f = mouthBay.frame
  spawnAt.x = f.mouth.x
  spawnAt.y = f.mouth.y
  spawnAt.z = f.mouth.z
  // Ship axes: right +X, up +Y, nose −Z; the columns are their world images (right, up, −out).
  quatFromMatrix(f.right.x, f.up.x, -f.out.x, f.right.y, f.up.y, -f.out.y, f.right.z, f.up.z, -f.out.z, spawnRot)
  return true
}

/** True when `slot`'s step wants a real ship (see the file header). Allocation-free. */
export function wantsRealShip(s: ContactStore, slot: number, cfg: TierConfig): boolean {
  const kind = s.kind[slot]
  if (kind === STEP_KIND.mining || kind === STEP_KIND.holding) return true
  if (kind !== STEP_KIND.leg) return false
  const x = s.x[slot]
  const y = s.y[slot]
  const z = s.z[slot]
  let dx = x - s.fx[slot]
  let dy = y - s.fy[slot]
  let dz = z - s.fz[slot]
  if (dx * dx + dy * dy + dz * dz < cfg.cruiseClearM * cfg.cruiseClearM) return true
  dx = x - s.tx[slot]
  dy = y - s.ty[slot]
  dz = z - s.tz[slot]
  return dx * dx + dy * dy + dz * dz < cfg.approachDropM * cfg.approachDropM
}

/** Despawns `slot`'s real ship (if any) and makes it a contact again. Rare. */
export function demote(sim: Sim, env: TierEnv, st: TierState, slot: number): void {
  const s = env.store
  const now = s.time[0]
  const eid = s.eid[slot]
  if (eid < 0) return
  s.eid[slot] = -1
  st.real -= st.eidWeight[eid]
  if (entityExists(sim.world, eid)) {
    despawnShip(sim, eid)
    commitRemovals(sim.world)
  }
  // A leg continues from where the ship is, over the time it has left.
  if (s.kind[slot] === STEP_KIND.leg && s.t1[slot] > now) {
    s.fx[slot] = s.x[slot]
    s.fy[slot] = s.y[slot]
    s.fz[slot] = s.z[slot]
    s.t0[slot] = now
  }
}

/** Spawns the real ship for contact `slot`; false when its def is unknown. Rare. */
export function promote(sim: Sim, env: TierEnv, st: TierState, slot: number): boolean {
  const s = env.store
  const def = env.defIndexOf(s.shipDef[slot])
  if (def < 0) {
    const key = s.shipDef[slot]
    if (!st.warned.has(key)) {
      st.warned.add(key)
      logger.warn(`traffic: unknown ship def '${key}'; the agent stays a contact`)
    }
    return false
  }
  // A moor-band hull leaving a station starts at its mooring-ring slot, never inside the bay.
  const moors = moorsAt(sim.defs[def])
  const undockM = moors ? moorSpawnPoint(env.pilot, slot, spawnAt) : -1
  // SC-5d1: an S/M still inside the station it leaves spawns at the bay mouth, nose out.
  const atMouth = !moors && mouthSpawn(env, slot)
  const x = undockM >= 0 || atMouth ? spawnAt.x : s.x[slot]
  const y = undockM >= 0 || atMouth ? spawnAt.y : s.y[slot]
  const z = undockM >= 0 || atMouth ? spawnAt.z : s.z[slot]
  // M10 P4b4b: a kitted agent flies its kit def (loadout, overlay, quirk); its weight and strategic hull stay the base's.
  const kit = env.kitOf?.(slot) ?? null
  const k = kit === null ? -1 : kitDefIndex(sim, def, kit)
  const fly = k < 0 ? def : k
  const eid = spawnShip(sim, fly, x, y, z, false, atMouth ? { team: s.team[slot], faOn: true, rotation: spawnRot } : { team: s.team[slot], faOn: true })
  Hull.value[eid] = Math.max(1, s.hp[slot] * sim.defs[fly].hull)
  if (kit !== null && k >= 0) setKitName(eid, fly, kitShipName(kit, sim.defs[def], s.ids[slot]))
  addComponent(sim.world, eid, Agent)
  addComponent(sim.world, eid, TrafficPilot)
  Agent.index[eid] = slot
  s.eid[slot] = eid
  const w = shipWeight(sim, st, def)
  st.eidWeight[eid] = w
  st.real += w
  if (undockM >= 0) beginTrafficPilot(env.pilot, eid, slot, undockM)
  else beginTrafficPilot(env.pilot, eid, slot)
  env.onPromoted?.(eid, slot)
  return true
}

/**
 * One tier step at Reach time `store.time[0]` around the living player (`sim.playerEid`; none: no promotion or distance demotion). Syncs the real ships, retires the done ones,
 * demotes and promotes (see the file header). Allocation-free on a quiet tick.
 */
export function stepTiers(sim: Sim, env: TierEnv, st: TierState): void {
  const s = env.store
  const p = sim.playerEid
  const player = p >= 0 && Life.alive[p] === 1
  // Read here, not passed in: a float64 argument is boxed on every call (D-014).
  const px = player ? Pos64.x[p] : 0
  const py = player ? Pos64.y[p] : 0
  const pz = player ? Pos64.z[p] : 0
  const cfg = env.cfg
  const demote2 = cfg.demoteM * cfg.demoteM
  const promote2 = cfg.promoteM * cfg.promoteM
  let n = 0
  // The farthest calm real ship that is not the player's own: the one an own candidate may replace at the cap.
  let evict = -1
  let evictD = -1
  for (let i = 0; i < s.count; i++) {
    const eid = s.eid[i]
    if (eid >= 0) {
      if (Life.alive[eid] !== 1 || !entityExists(sim.world, eid)) {
        // Killed: the kill reader (sync.ts) reports and removes it; a stale id just falls back to a contact.
        s.eid[i] = -1
        st.real -= st.eidWeight[eid]
        continue
      }
      s.x[i] = Pos64.x[eid]
      s.y[i] = Pos64.y[eid]
      s.z[i] = Pos64.z[eid]
      const hull = sim.defs[Ship.defIndex[eid]].hull
      s.hp[i] = hull > 0 ? Hull.value[eid] / hull : 0
      const phase = TrafficPilot.phase[eid]
      if (phase === TRAFFIC_PHASE.done) {
        markArrived(s, i)
        demote(sim, env, st, i)
        continue
      }
      const dx = s.x[i] - px
      const dy = s.y[i] - py
      const dz = s.z[i] - pz
      const calm = sim.time - s.combatAt[i] >= cfg.demoteCalmS
      const d2 = dx * dx + dy * dy + dz * dz
      if (phase === TRAFFIC_PHASE.cruise || (player && calm && d2 > demote2)) {
        demote(sim, env, st, i)
        continue
      }
      if (calm && s.own[i] === 0 && d2 > evictD) {
        evict = i
        evictD = d2
      }
      continue
    }
    // Docked, or a stand-by contact hidden by its station's cap (BL-1a): inside the station, never promoted.
    if (!player || isDockedLike(s, i) || s.reported[i] === 1) continue
    const dx = s.x[i] - px
    const dy = s.y[i] - py
    const dz = s.z[i] - pz
    const d2 = dx * dx + dy * dy + dz * dz
    if (d2 > promote2 || !wantsRealShip(s, i, cfg)) continue
    // Insertion sort by distance (the candidates within 20 km are few); own ships key below every other (d2 ≤ promote2).
    const key = s.own[i] === 1 ? d2 - promote2 - 1 : d2
    let k = n++
    while (k > 0 && st.candD[k - 1] > key) {
      st.cand[k] = st.cand[k - 1]
      st.candD[k] = st.candD[k - 1]
      k--
    }
    st.cand[k] = i
    st.candD[k] = key
  }
  let k = 0
  for (; k < n; k++) {
    const c = st.cand[k]
    const def = env.defIndexOf(s.shipDef[c])
    // A moor-band ship leaving a station whose bay isn't ready stays a contact (M10 P3 W4b); it blocks no one.
    if (def >= 0 && moorsAt(sim.defs[def]) && moorSpawnPoint(env.pilot, c, spawnAt) === MOOR_WAIT) continue
    if (st.real + shipWeight(sim, st, def) > cfg.maxRealShips) break
    promote(sim, env, st, c)
  }
  // At the cap: the nearest own ship still waiting replaces the farthest calm stranger when that frees room for its
  // weight (one swap per tick, rare).
  if (k < n && evict >= 0 && s.own[st.cand[k]] === 1) {
    const def = env.defIndexOf(s.shipDef[st.cand[k]])
    if (def >= 0 && st.real - st.eidWeight[s.eid[evict]] + shipWeight(sim, st, def) <= cfg.maxRealShips) {
      demote(sim, env, st, evict)
      promote(sim, env, st, st.cand[k])
    }
  }
}

/** Despawns every real ship (the system was left, or the subsystem stops). */
export function despawnAll(sim: Sim, env: TierEnv, st: TierState): void {
  const s = env.store
  for (let i = 0; i < s.count; i++) if (s.eid[i] >= 0) demote(sim, env, st, i)
  st.real = 0
}

